import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { IoClose } from 'react-icons/io5';
import { RoleDefinition, RoleShell } from 'src/types/permissions.types';
import { ROLE_SHELLS } from 'src/config/permissionCatalog';
import { RoleInput, toRoleName } from 'src/services/role.service';
import { goldButtonSx, SHELL_ACCENTS } from './RoleUi';

interface CreateRoleModalProps {
  open: boolean;
  onClose: () => void;
  roles: RoleDefinition[];
  /** Pre-selects a role to copy, for "Duplicate". */
  initialSource?: string;
  onSubmit: (role: RoleInput) => Promise<unknown>;
  isSubmitting?: boolean;
}

const BLANK = '__blank__';

/** Picks one of the three dashboards; shared by create and settings. */
export function ShellPicker({
  value,
  onChange,
  disabled,
}: {
  value: RoleShell;
  onChange: (shell: RoleShell) => void;
  disabled?: boolean;
}) {
  return (
    <Stack spacing={1} role="radiogroup" aria-label="Dashboard">
      {ROLE_SHELLS.map((shell) => {
        const selected = shell.value === value;
        const { color } = SHELL_ACCENTS[shell.value];
        return (
          <Box
            key={shell.value}
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && onChange(shell.value)}
            onKeyDown={(e) => {
              if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onChange(shell.value);
              }
            }}
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: '1px solid',
              borderColor: selected ? color : '#E2E8F0',
              bgcolor: selected ? alpha(color, 0.06) : '#FFFFFF',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled && !selected ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              transition: 'all 0.15s ease',
              '&:hover': disabled ? undefined : { borderColor: color },
              '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
            }}
          >
            <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: alpha(color, 0.12), color, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <Icon icon={shell.icon} width={18} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#0F172A" fontSize="0.8125rem">
                {shell.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontSize="0.72rem">
                {shell.description}
              </Typography>
            </Box>
            {selected && <Icon icon="solar:check-circle-bold" width={20} color={color} />}
          </Box>
        );
      })}
    </Stack>
  );
}

export function CreateRoleModal({ open, onClose, roles, initialSource, onSubmit, isSubmitting }: CreateRoleModalProps) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [shell, setShell] = useState<RoleShell>('/admin');
  const [source, setSource] = useState<string>(BLANK);
  const [error, setError] = useState<string | null>(null);

  const sourceRole = roles.find((r) => r.name === source);
  const key = toRoleName(label);

  useEffect(() => {
    if (!open) return;
    const initial = roles.find((r) => r.name === initialSource);
    setLabel(initial ? `${initial.label} copy` : '');
    setDescription('');
    setSource(initial ? initial.name : BLANK);
    setShell(initial ? initial.base_route : '/admin');
    setError(null);
  }, [open, initialSource]);

  const handleSourceChange = (value: string) => {
    setSource(value);
    const role = roles.find((r) => r.name === value);
    if (role) setShell(role.base_route);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!key) {
      setError('Give the role a name, for example Dean.');
      return;
    }
    if (roles.some((r) => r.name === key)) {
      setError(`A role with the key ${key} already exists.`);
      return;
    }
    try {
      setError(null);
      await onSubmit({
        name: key,
        label: label.trim(),
        description: description.trim() || null,
        base_route: shell,
        permissions: sourceRole ? sourceRole.permissions.filter((p) => p !== '*') : [],
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create role.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1A202C">
            Create New Role
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add a role such as Dean or Counselor. You can fine-tune its permissions next.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <IoClose />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ py: 2 }}>
          {error && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FFF5F5', color: '#E53E3E', borderRadius: 1, fontSize: '0.875rem' }}>{error}</Box>
          )}

          <Stack spacing={2.5}>
            <TextField
              label="Role Name"
              placeholder="e.g. Dean, Counselor, Vice Principal"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              fullWidth
              size="small"
              required
              autoFocus
              InputProps={{
                endAdornment: key ? (
                  <Chip label={key} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.68rem', height: 20, bgcolor: '#F1F5F9' }} />
                ) : undefined,
              }}
              helperText="The key on the right is what the system stores. It cannot change later."
            />

            <TextField
              label="Description (Optional)"
              placeholder="What is this role for?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="role-source-label">Start From</InputLabel>
              <Select
                labelId="role-source-label"
                label="Start From"
                value={source}
                onChange={(e) => handleSourceChange(e.target.value)}
              >
                <MenuItem value={BLANK}>Blank role (no permissions)</MenuItem>
                {roles
                  .filter((r) => !r.permissions.includes('*'))
                  .map((r) => (
                    <MenuItem key={r.name} value={r.name}>
                      Copy of {r.label} ({r.permissions.length} permissions)
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="caption" fontWeight={700} color="#64748B" letterSpacing={0.5} sx={{ display: 'block', mb: 1 }}>
                DASHBOARD
              </Typography>
              <ShellPicker value={shell} onChange={setShell} />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting} sx={goldButtonSx}>
            {isSubmitting ? 'Creating...' : 'Create Role'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
